import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as Tooltip from '@radix-ui/react-tooltip'
import * as Slider from '@radix-ui/react-slider'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUp, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { PiGavel, PiSmiley } from 'react-icons/pi'
import { LuSection, LuPencil } from 'react-icons/lu'
import { FaBalanceScaleLeft } from 'react-icons/fa'
import classNames from 'classnames'

interface SubmissionOption {
  id: string
  icon: React.ReactNode
  tooltip: string
  iconSize?: string
  sliderLabel?: string
  sliderMin?: number
  sliderMax?: number
  sliderDefault?: number
  sliderStep?: number
  sliderLabels?: string[]
}

interface AnimatedSubmitButtonProps {
  readonly disabled: boolean
  readonly loading: boolean
  readonly onSubmit: (_optionId?: string, _sliderValue?: number, _sliderLabel?: string) => void
  readonly className?: string
  readonly onInteraction?: () => void
}

const submissionOptions: SubmissionOption[] = [
  {
    id: 'legal-language',
    icon: <PiGavel />,
    tooltip: 'Add legal language',
    iconSize: '1.2rem',
    sliderLabel: 'Legal Formality',
    sliderMin: 1,
    sliderMax: 10,
    sliderDefault: 5,
    sliderStep: 1,
    sliderLabels: [
      'Very Casual',
      'Casual',
      'Informal',
      'Slightly Informal',
      'Neutral',
      'Slightly Formal',
      'Formal',
      'Very Formal',
      'Highly Formal',
      'Most Formal',
    ],
  },
  {
    id: 'legal-citations',
    icon: <LuSection />,
    tooltip: 'Add legal citations',
    iconSize: '1.2rem',
    sliderLabel: 'Citation Density',
    sliderMin: 1,
    sliderMax: 10,
    sliderDefault: 5,
    sliderStep: 1,
    sliderLabels: [
      'Minimal Citations',
      'Few Citations',
      'Light Citations',
      'Some Citations',
      'Moderate Citations',
      'Good Citations',
      'Strong Citations',
      'Heavy Citations',
      'Extensive Citations',
      'Comprehensive Citations',
    ],
  },
  {
    id: 'professional-weighting',
    icon: <FaBalanceScaleLeft />,
    tooltip: 'Defendant to plaintiff friendly',
    iconSize: '1.2rem',
    sliderLabel: 'Tone Balance',
    sliderMin: 1,
    sliderMax: 10,
    sliderDefault: 5,
    sliderStep: 1,
    sliderLabels: [
      'Most Defendant-Friendly',
      'Very Defendant-Friendly',
      'Defendant-Friendly',
      'Slightly Defendant-Friendly',
      'Balanced',
      'Slightly Plaintiff-Friendly',
      'Plaintiff-Friendly',
      'Very Plaintiff-Friendly',
      'Strongly Plaintiff-Friendly',
      'Most Plaintiff-Friendly',
    ],
  },
  {
    id: 'tone-adjustment',
    icon: <PiSmiley />,
    tooltip: 'Adjust the tone',
    iconSize: '2rem',
    sliderLabel: 'Friendliness',
    sliderMin: 1,
    sliderMax: 10,
    sliderDefault: 5,
    sliderStep: 1,
    sliderLabels: [
      'Least Friendly',
      'Less Friendly',
      'Somewhat Unfriendly',
      'Slightly Unfriendly',
      'Neutral',
      'Slightly Friendly',
      'Friendly',
      'More Friendly',
      'Very Friendly',
      'Most Friendly',
    ],
  },
  {
    id: 'default',
    icon: <FontAwesomeIcon icon={faArrowUp} />,
    tooltip: 'Suggest edits',
    iconSize: '1rem',
  },
]

function AnimatedSubmitButton({
  disabled,
  loading,
  onSubmit,
  className,
  onInteraction,
}: AnimatedSubmitButtonProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [buttonHeight, setButtonHeight] = React.useState(38)
  const [selectedOption, setSelectedOption] = React.useState<SubmissionOption | null>(null)
  const [sliderValue, setSliderValue] = React.useState(5)
  const closeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const popoverRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
      }
    }
  }, [])

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        popoverRef.current &&
        buttonRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setSelectedOption(null)
        clearCloseTimeout()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  React.useEffect(() => {
    if (buttonRef.current) {
      setButtonHeight(buttonRef.current.offsetHeight)
    }
  }, [])

  React.useEffect(() => {
    if (selectedOption) {
      setSliderValue(selectedOption.sliderDefault || 5)
    }
  }, [selectedOption])

  const getSliderLabel = (value: number): string => {
    if (!selectedOption?.sliderLabels) {
      return value.toString()
    }

    const labelIndex = value - (selectedOption.sliderMin || 1)
    return selectedOption.sliderLabels[labelIndex] || value.toString()
  }

  const initialYOffset = `-${buttonHeight / 2}px`

  const buttonClassNames = React.useMemo(
    () =>
      classNames(
        {
          'circle-btn': true,
          'disabled': disabled,
        },
        className
      ),
    [disabled, className]
  )

  const iconClassNames = React.useMemo(
    () =>
      classNames({
        'fa-spin': loading,
      }),
    [loading]
  )

  const handleOptionClick = (optionId: string) => {
    if (optionId === 'default') {
      onSubmit('default', 5, 'General Edit')
      setIsOpen(false)
      setSelectedOption(null)
    } else {
      const option = submissionOptions.find(opt => opt.id === optionId)
      if (option) {
        setSelectedOption(option)
      }
    }
  }

  const handleSliderSubmit = () => {
    if (selectedOption) {
      const sliderLabel = getSliderLabel(sliderValue)
      onSubmit(selectedOption.id, sliderValue, sliderLabel)
      setIsOpen(false)
      setSelectedOption(null)
    }
  }

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  const scheduleClose = () => {
    clearCloseTimeout()
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false)
      setSelectedOption(null)
      closeTimeoutRef.current = null
    }, 1000)
  }

  const handleMouseEnter = () => {
    if (!disabled) {
      clearCloseTimeout()
      setIsOpen(true)
      onInteraction?.()
    }
  }

  const handleMouseLeave = () => {
    scheduleClose()
  }

  const handlePopoverMouseEnter = () => {
    clearCloseTimeout()
  }

  const handlePopoverMouseLeave = () => {
    if (!selectedOption) {
      scheduleClose()
    }
  }

  const renderSliderInterface = () => {
    const min = selectedOption?.sliderMin || 1
    const max = selectedOption?.sliderMax || 10
    const step = selectedOption?.sliderStep || 1
    const trackHeight = 200

    const steps = []
    for (let i = min; i <= max; i += step) {
      steps.push(i)
    }

    return (
      <motion.div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            overflow: 'visible',
          }}
        >
          {/* Background layer for step markers */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              height: `${trackHeight}px`,
              width: '1px',
              zIndex: 0,
              pointerEvents: 'none',
            }}
          >
            {steps.map(stepValue => {
              const percentage = (stepValue - min) / (max - min)
              // Account for thumb radius (2.5rem = 40px, so radius = 20px)
              const thumbRadius = 20
              const usableHeight = trackHeight - 2 * thumbRadius
              const position = thumbRadius + percentage * usableHeight

              return (
                <div
                  key={stepValue}
                  style={{
                    position: 'absolute',
                    top: `${position}px`,
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.8)',
                    pointerEvents: 'none',
                  }}
                />
              )
            })}
          </div>

          <Slider.Root
            value={[sliderValue]}
            onValueChange={value => setSliderValue(value[0])}
            orientation='vertical'
            min={min}
            max={max}
            step={step}
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              height: `${trackHeight}px`,
              cursor: 'pointer',
            }}
          >
            <Slider.Track
              style={{
                position: 'relative',
                flexGrow: 1,
                width: '3px',
                borderRadius: '2px',
              }}
            >
              <Slider.Range
                style={{
                  position: 'absolute',
                  width: '100%',
                  borderRadius: '2px',
                }}
              />
            </Slider.Track>
            <Slider.Thumb
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                outline: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                color: 'white',
                fontSize: selectedOption?.iconSize || '1rem',
                backdropFilter: 'blur(10px)',
                position: 'relative',
                zIndex: 1,
              }}
            >
              {selectedOption?.icon}
              <div
                style={{
                  position: 'absolute',
                  right: '3rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: '#1f1f1f',
                  color: 'white',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.375rem',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  whiteSpace: 'nowrap',
                  zIndex: 10001,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  pointerEvents: 'none',
                }}
              >
                {getSliderLabel(sliderValue)}
                {/* Tooltip arrow pointing to the thumb */}
                <div
                  style={{
                    position: 'absolute',
                    right: '-4px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 0,
                    height: 0,
                    borderLeft: '4px solid #1f1f1f',
                    borderTop: '4px solid transparent',
                    borderBottom: '4px solid transparent',
                  }}
                />
              </div>
            </Slider.Thumb>
          </Slider.Root>
        </div>

        {/* Submit button */}
        <motion.button
          onClick={handleSliderSubmit}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '2.5rem',
            height: '2.5rem',
            background: 'rgba(255, 255, 255, 0.9)',
            color: 'var(--bs-primary)',
            border: 'none',
            borderRadius: '50%',
            padding: '0',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            marginTop: '0.5rem',
          }}
          whileHover={{ backgroundColor: 'white' }}
          whileTap={{ scale: 0.95 }}
          disabled={loading}
        >
          {loading ? (
            <FontAwesomeIcon icon={faSpinner} className='fa-spin' />
          ) : (
            <FontAwesomeIcon icon={faArrowUp} />
          )}
        </motion.button>
      </motion.div>
    )
  }

  const renderOptionGrid = () => (
    <motion.div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem',
        alignItems: 'center',
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.15, duration: 0.3 }}
    >
      {submissionOptions.map((submissionOption, index) => {
        return (
          <Tooltip.Root key={submissionOption.id} delayDuration={300}>
            <Tooltip.Trigger asChild>
              <motion.button
                className='submission-option submission-option--primary'
                onClick={() => handleOptionClick(submissionOption.id)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: 0.05 * index,
                  duration: 0.3,
                  ease: [0.16, 1, 0.3, 1],
                }}
                whileHover={{
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                }}
                whileTap={{ scale: 0.95 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '2.25rem',
                  height: '2.25rem',
                  borderRadius: '50%',
                  border: 'none',
                  background: 'rgba(255, 255, 255, 0.2)',
                  cursor: 'pointer',
                  margin: '0',
                  color: 'white',
                  fontSize: submissionOption.iconSize || '1rem',
                  backdropFilter: 'blur(10px)',
                }}
              >
                {submissionOption.id === 'default' && loading ? (
                  <FontAwesomeIcon icon={faSpinner} className={iconClassNames} />
                ) : (
                  submissionOption.icon
                )}
              </motion.button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className='TooltipContent'
                side='left'
                sideOffset={8}
                style={{
                  backgroundColor: '#1f1f1f',
                  color: '#ffffff',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.375rem',
                  fontSize: '0.75rem',
                  lineHeight: '1.25',
                  maxWidth: '200px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  zIndex: 10000,
                  animationDuration: '400ms',
                  animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              >
                {submissionOption.tooltip}
                <Tooltip.Arrow
                  style={{
                    fill: '#1f1f1f',
                    width: '8px',
                    height: '4px',
                  }}
                />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        )
      })}
    </motion.div>
  )

  return (
    <Tooltip.Provider>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-end' }}>
        <motion.button
          ref={buttonRef}
          type='button'
          className={buttonClassNames}
          onClick={() => handleOptionClick('default')}
          disabled={disabled}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          animate={{
            opacity: isOpen ? 0 : 1,
            scale: isOpen ? 0.8 : 1,
          }}
          transition={{
            duration: 0.3,
            ease: [0.16, 1, 0.3, 1],
          }}
          style={{
            position: 'relative',
            zIndex: isOpen ? 1 : 2,
          }}
        >
          {loading ? <FontAwesomeIcon icon={faSpinner} className={iconClassNames} /> : <LuPencil />}
        </motion.button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={popoverRef}
              initial={{
                scale: 0,
                y: initialYOffset,
              }}
              animate={{
                scale: 1,
                y: 0,
                opacity: 1,
              }}
              exit={{
                scale: 0,
                y: initialYOffset,
              }}
              transition={{
                duration: 0.4,
                ease: [0.16, 1, 0.3, 1],
              }}
              style={{
                position: 'absolute',
                bottom: 0,
                left: '50%',
                backgroundColor: 'var(--bs-primary)',
                border: '1px solid var(--bs-primary)',
                borderRadius: '999px',
                padding: '0.75rem 0.5rem',
                boxShadow:
                  '0 10px 25px -3px rgba(0, 0, 0, 0.2), 0 8px 10px -3px rgba(0, 0, 0, 0.1)',
                zIndex: 9999,
                overflow: 'visible',
                transformOrigin: 'bottom center',
                x: '-50%',
              }}
              onMouseEnter={handlePopoverMouseEnter}
              onMouseLeave={handlePopoverMouseLeave}
            >
              {selectedOption ? renderSliderInterface() : renderOptionGrid()}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Tooltip.Provider>
  )
}

export default AnimatedSubmitButton
